/**
 * v3.18-1267 PAYMENT-007G — Shared Charge Card Dialog (saved-card only).
 *
 * The dialog now REQUIRES the operator to pick a saved authorization —
 * manual PAN entry has been removed from the Payment Center Charge Card
 * workflow. Credit Card Authorizations are the sole system of record.
 *
 * Flow:
 *   1. Dialog opens → load client context + terminals + saved cards.
 *   2. Operator picks a saved authorization.
 *   3. Operator picks Terminal 1 or Terminal 2 (or dual buttons.)
 *   4. Operator enters/confirms amount + optional memo.
 *   5. Review → Confirm & Charge → POST /payments/engine/charge with
 *      credit_card_authorization_id. Backend loads the PAN from the
 *      stored authorization and routes it through the selected adapter.
 *
 * Same dialog is mounted from both:
 *   • Payment Center → Charge Card tab (no preferred auth id).
 *   • Client profile → Credit Card Authorization → Charge — T1/T2
 *     (preferred terminal + auth id preselected).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import useLiveActivityClaim from '../hooks/useLiveActivityClaim';   // v3.18-1343 LAC-VERIFY
import {
  Loader2, CreditCard, ShieldCheck, AlertTriangle, ExternalLink, Info,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';

const fmtMoney = (amt, ccy) => {
  const n = typeof amt === 'string' ? parseFloat(amt) : amt;
  if (n == null || Number.isNaN(n)) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: (ccy || 'USD').toUpperCase(),
    }).format(n);
  } catch { return `${(ccy || 'USD').toUpperCase()} ${n.toFixed(2)}`; }
};

export const ChargeCardDialog = ({
  open, onOpenChange, clientId, dealId, defaultAmount, onCharged,
  preferredTerminalId, creditCardAuthorizationId,
}) => {
  const [step, setStep] = useState('form'); // form → confirm → submitting → result
  const [ctx, setCtx] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [savedCards, setSavedCards] = useState([]);
  const [loading, setLoading] = useState(false);

  const [terminalId, setTerminalId] = useState('');
  const [selectedAuthId, setSelectedAuthId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [memo, setMemo] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [selectedDealId, setSelectedDealId] = useState(dealId || '');
  const [result, setResult] = useState(null);
  const [chargeError, setChargeError] = useState(null);

  // v3.18-1343 LAC-VERIFY — advertise a "collecting payment" claim
  // so directors watching the client see who is handling the charge.
  // Metadata deliberately excludes card numbers, CVV, and any
  // processor secrets — only operational context (deal_id, amount).
  useLiveActivityClaim({
    clientId,
    workflow: 'collecting_payment',
    metadata: { deal_id: selectedDealId || dealId || null },
    enabled:  !!(open && clientId),
  });
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setStep('form'); setCtx(null); setTerminals([]); setSavedCards([]);
    setTerminalId(''); setSelectedAuthId(creditCardAuthorizationId || '');
    setAmount(''); setCurrency('USD'); setMemo(''); setBillingZip('');
    setSelectedDealId(dealId || ''); setResult(null); setChargeError(null);
    setSubmitting(false);
  }, [dealId, creditCardAuthorizationId]);

  useEffect(() => {
    if (!open || !clientId) return;
    reset();
    setLoading(true);
    Promise.all([
      api.get(`/payments/engine/clients/${clientId}/context`),
      api.get('/payments/engine/terminals'),
      api.get(`/clients/${clientId}/credit-card-auth/list`).catch(() => ({ data: { records: [] } })),
    ]).then(([ctxRes, termRes, cardsRes]) => {
      setCtx(ctxRes.data);
      setTerminals(termRes.data?.terminals || []);
      const cards = (cardsRes.data?.records || []).filter(r => r.can_reveal);
      setSavedCards(cards);

      if (ctxRes.data?.auto_selected_deal_id) setSelectedDealId(ctxRes.data.auto_selected_deal_id);
      else if (dealId) setSelectedDealId(dealId);

      const termList = termRes.data?.terminals || [];
      const preferred = preferredTerminalId
        ? termList.find(t => t.terminal_id === preferredTerminalId && t.enabled)
        : null;
      const firstEnabled = preferred || termList.find(t => t.enabled);
      if (firstEnabled) setTerminalId(firstEnabled.terminal_id);

      // Preselect saved auth: caller-provided → newest chargeable card
      let preselectedCardId = '';
      if (creditCardAuthorizationId && cards.some(c => c.id === creditCardAuthorizationId)) {
        preselectedCardId = creditCardAuthorizationId;
      } else if (cards.length === 1) {
        preselectedCardId = cards[0].id;
      }
      if (preselectedCardId) setSelectedAuthId(preselectedCardId);

      // v3.18-1269 PAYMENT-007I — Amount precedence:
      //   1. Caller-provided defaultAmount (explicit override).
      //   2. The selected authorization's stored amount (Enter New CC
      //      captured this — use it so the operator does not re-key).
      //   3. The client's outstanding balance.
      const preselectedCard = preselectedCardId
        ? cards.find(c => c.id === preselectedCardId)
        : null;
      if (defaultAmount != null && defaultAmount !== '') {
        setAmount(String(defaultAmount));
      } else if (preselectedCard?.amount) {
        setAmount(String(Number(preselectedCard.amount).toFixed(2)));
      } else if (ctxRes.data?.outstanding_balance) {
        setAmount(String(ctxRes.data.outstanding_balance.toFixed(2)));
      }
      // v3.18-1272 PAYMENT-007L — Prepopulate billing ZIP from the
      // stored authorization when present so operator never re-enters.
      if (preselectedCard?.billing_zip) {
        setBillingZip(String(preselectedCard.billing_zip));
      }
    }).catch((e) => {
      const m = e?.response?.data?.detail || 'Failed to load client context';
      toast.error(typeof m === 'string' ? m : 'Failed to load client context');
    }).finally(() => setLoading(false));
  }, [open, clientId, dealId, defaultAmount, reset, preferredTerminalId, creditCardAuthorizationId]);

  const selectedTerminal = useMemo(
    () => terminals.find(t => t.terminal_id === terminalId),
    [terminals, terminalId],
  );
  const selectedDeal = useMemo(
    () => (ctx?.active_deals || []).find(d => d.id === selectedDealId),
    [ctx, selectedDealId],
  );
  const selectedCard = useMemo(
    () => savedCards.find(c => c.id === selectedAuthId),
    [savedCards, selectedAuthId],
  );

  // v3.18-1272 PAYMENT-007L — when the operator picks a different saved
  // card, prefill billing ZIP from the newly selected authorization if
  // that card has one AND the operator has not already typed one.
  useEffect(() => {
    if (!selectedCard) return;
    if (billingZip && billingZip.trim()) return;
    if (selectedCard.billing_zip) setBillingZip(String(selectedCard.billing_zip));
  }, [selectedAuthId, selectedCard, billingZip]);

  // v3.18-1272 PAYMENT-007L — Terminal-specific requirements.
  const hasActiveDeal = !!selectedDealId || (ctx?.active_deals?.length || 0) > 0;
  const isEmsSelected = terminalId === 'ptc_terminal_2';
  const emsRequiresZip = isEmsSelected;
  const emsBlockedNoDeal = isEmsSelected && !hasActiveDeal;
  const emsMissingZip = emsRequiresZip && !billingZip.trim();

  const noSavedCards = !loading && savedCards.length === 0;
  const canProceed = (
    !!terminalId
    && !!selectedAuthId
    && !!amount && parseFloat(amount) > 0
    && !!clientId
    && !emsBlockedNoDeal
    && !emsMissingZip
  );

  const submitCharge = async (terminalOverride) => {
    if (!terminalOverride && !canProceed) return;
    const effectiveTerminal = terminalOverride || terminalId;
    if (!effectiveTerminal) { toast.error('Select a terminal'); return; }
    if (!selectedAuthId) { toast.error('Select a saved card'); return; }
    setChargeError(null);
    setSubmitting(true);
    setStep('submitting');
    try {
      const res = await api.post('/payments/engine/charge', {
        client_id: clientId,
        deal_id: selectedDealId || null,
        amount: parseFloat(amount),
        currency: (currency || 'USD').toUpperCase(),
        terminal_id: effectiveTerminal,
        memo: memo || null,
        credit_card_authorization_id: selectedAuthId,
        billing_zip: billingZip.trim() || null,
      });
      // v3.18-1269 PAYMENT-007I — preserve dialog state on ANY failure.
      // Provider-side rejections (status=error) return to the form step
      // with an inline error banner and all fields intact so the operator
      // can retry without re-entry.
      if (res.data?.status === 'error') {
        setChargeError({
          message: res.data?.error_message || 'Payment provider rejected the request.',
          code: res.data?.error_code || null,
          terminal_label: res.data?.terminal_label || null,
        });
        setStep('form');
        return;
      }
      setResult(res.data);
      setStep('result');
      toast.success(`Payment initiated on ${res.data?.terminal_label || 'terminal'}`);
      onCharged?.(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let message = 'Charge failed.';
      let code = null;
      // v3.18-1272 PAYMENT-007L — the 422 pre-flight-validation-failed
      // response uses a structured `detail` object. Surface the list
      // of validation errors so the operator can fix them in place.
      if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        message = detail.errors.join(' ');
        code = detail.error_code || null;
      } else if (typeof detail === 'string') {
        message = detail;
      } else if (e?.message) {
        message = e.message;
      }
      setChargeError({ message, code, terminal_label: null });
      setStep('form');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="charge-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />Charge Card
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-8">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading client…</span>
          </div>
        ) : step === 'form' ? (
          <div className="space-y-4">
            {chargeError ? (
              <div
                className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900 flex items-start gap-2"
                data-testid="charge-card-error-banner"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-700" />
                <div className="flex-1 space-y-1">
                  <div className="font-semibold">
                    Charge failed{chargeError.terminal_label ? ` on ${chargeError.terminal_label}` : ''}
                  </div>
                  <div className="text-xs">{chargeError.message}</div>
                  {chargeError.code ? (
                    <div className="text-[10px] font-mono text-red-700/80">
                      code: {chargeError.code}
                    </div>
                  ) : null}
                  <div className="text-[10px] text-red-700/80 pt-1">
                    Your selections are preserved. Fix the issue and press Review again.
                  </div>
                </div>
              </div>
            ) : null}
            {/* Client summary */}
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs">
              <div className="grid grid-cols-3 gap-y-1">
                <div className="text-slate-500">Client</div>
                <div className="col-span-2 font-medium">{ctx?.client?.full_name || '—'}</div>
                <div className="text-slate-500">Outstanding</div>
                <div className="col-span-2 font-medium" data-testid="charge-card-outstanding">
                  {fmtMoney(ctx?.outstanding_balance)}
                </div>
                <div className="text-slate-500">Collected</div>
                <div className="col-span-2">{fmtMoney(ctx?.total_collected)}</div>
              </div>
            </div>

            {/* Deal */}
            <div>
              <Label>Deal</Label>
              {(ctx?.active_deals || []).length === 0 ? (
                <div className="text-xs text-slate-500 italic mt-1">
                  No active deals — payment will be recorded without a deal linkage.
                </div>
              ) : (ctx?.active_deals || []).length === 1 ? (
                <div
                  className="mt-1 text-sm text-slate-700 border border-slate-200 rounded-md px-3 py-2 bg-slate-50"
                  data-testid="charge-card-deal-single"
                >
                  {ctx.active_deals[0].deal_name || ctx.active_deals[0].id}
                </div>
              ) : (
                <select
                  className="w-full border rounded-md h-9 px-3 text-sm mt-1"
                  value={selectedDealId || ''}
                  onChange={(e) => setSelectedDealId(e.target.value)}
                  data-testid="charge-card-deal-select"
                >
                  <option value="">— select deal —</option>
                  {ctx.active_deals.map(d => (
                    <option key={d.id} value={d.id}>{d.deal_name || d.id}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Saved card picker */}
            <div>
              <Label>Saved Card</Label>
              {noSavedCards ? (
                <div
                  className="mt-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2"
                  data-testid="charge-card-no-cards"
                >
                  <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <div>
                    No chargeable card on file. Add one from the client&apos;s{' '}
                    <strong>Credit Card Authorizations</strong> section, then return
                    to charge.
                  </div>
                </div>
              ) : (
                <div className="mt-1 space-y-1.5" data-testid="charge-card-saved-cards">
                  {savedCards.map((card) => {
                    const isSelected = selectedAuthId === card.id;
                    return (
                      <button
                        key={card.id}
                        type="button"
                        onClick={() => setSelectedAuthId(card.id)}
                        className={`w-full text-left border rounded-md px-3 py-2 text-sm transition-colors ${
                          isSelected
                            ? 'border-slate-900 bg-slate-900 text-white'
                            : 'bg-white border-slate-300 hover:bg-slate-50'
                        }`}
                        data-testid={`charge-card-saved-card-${card.id}`}
                      >
                        <div className="font-mono font-semibold">
                          {card.card_masked || `•••• ${card.last4 || '----'}`}
                        </div>
                        <div className={`text-[10px] mt-0.5 ${isSelected ? 'text-slate-200' : 'text-slate-500'}`}>
                          {card.brand || 'Card'} · {card.name_on_card || '—'}
                          {card.amount ? ` · Auth amount ${fmtMoney(card.amount)}` : ''}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Terminal selector */}
            <div>
              <Label>Process with</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {terminals.map(t => {
                  const isSelected = terminalId === t.terminal_id;
                  const isEms = t.terminal_id === 'ptc_terminal_2';
                  // v3.18-1272 PAYMENT-007L — EMS T2 requires an active deal.
                  const blockedByDeal = isEms && !hasActiveDeal;
                  const disabled = !t.enabled || blockedByDeal;
                  return (
                    <button
                      key={t.terminal_id}
                      onClick={() => !disabled && setTerminalId(t.terminal_id)}
                      disabled={disabled}
                      title={
                        blockedByDeal
                          ? 'Terminal 2 requires an active Deal because EMS requires Deal ID (MDF2).'
                          : (!t.enabled ? 'Terminal is disabled in configuration.' : '')
                      }
                      className={`text-left border rounded-md px-3 py-2 text-sm transition-colors ${
                        disabled
                          ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                          : isSelected
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'bg-white border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`charge-card-terminal-${t.terminal_id}`}
                    >
                      <div className="font-semibold">{t.label}</div>
                      <div className="text-[10px] uppercase tracking-wider mt-0.5">
                        {t.role === 'primary' ? 'Primary' : 'Secondary'}
                        {!t.enabled ? ' — Disabled until configured' : ''}
                        {blockedByDeal ? ' — Requires active Deal (MDF2)' : ''}
                      </div>
                    </button>
                  );
                })}
              </div>
              {emsBlockedNoDeal ? (
                <div
                  className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900"
                  data-testid="charge-card-t2-blocked-no-deal"
                >
                  Terminal 2 requires an active Deal because EMS requires Deal ID (MDF2).
                  Create or link a Deal on the client before charging through Terminal 2.
                </div>
              ) : null}
            </div>

            {/* Amount + currency */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Amount</Label>
                <Input
                  type="number" step="0.01" min="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  data-testid="charge-card-amount"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Input
                  value={currency}
                  onChange={e => setCurrency(e.target.value)}
                  data-testid="charge-card-currency"
                />
              </div>
            </div>

            {/* v3.18-1272 PAYMENT-007L — Billing ZIP.
                Shown for every terminal; REQUIRED when EMS (T2) is
                selected. Prepopulated from the authorization when
                present so the operator never re-enters. */}
            <div>
              <Label>
                Billing ZIP
                {isEmsSelected ? (
                  <span className="text-red-600 ml-1">*</span>
                ) : (
                  <span className="text-slate-400 text-xs ml-1">(optional for Terminal 1)</span>
                )}
              </Label>
              <Input
                value={billingZip}
                onChange={e => setBillingZip(e.target.value)}
                placeholder={isEmsSelected ? 'Required for EMS AVS' : '12345'}
                inputMode="numeric"
                maxLength={10}
                data-testid="charge-card-billing-zip"
                className={emsMissingZip ? 'border-red-300 focus-visible:ring-red-500' : ''}
              />
              {selectedCard?.billing_zip_present ? (
                <p className="text-[10px] text-emerald-700 mt-0.5">
                  Prefilled from saved authorization.
                </p>
              ) : isEmsSelected ? (
                <p className="text-[10px] text-slate-500 mt-0.5">
                  Enter once — it will be saved on the authorization and reused for future charges.
                </p>
              ) : null}
            </div>

            {/* Memo */}
            <div>
              <Label>Memo (optional)</Label>
              <Input
                value={memo}
                onChange={e => setMemo(e.target.value)}
                data-testid="charge-card-memo"
              />
            </div>
          </div>
        ) : step === 'confirm' ? (
          <div className="space-y-4" data-testid="charge-card-confirm">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Confirm the charge below. This will initiate the payment on the selected terminal.</span>
            </div>
            <div className="rounded-md border border-slate-200 p-4 text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Client</span>
                <span className="font-medium">{ctx?.client?.full_name || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Deal</span>
                <span className="font-medium">
                  {selectedDeal?.deal_name || (selectedDealId ? selectedDealId : '—')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Card</span>
                <span className="font-mono font-medium">
                  {selectedCard?.card_masked || `•••• ${selectedCard?.last4 || '----'}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Terminal</span>
                <span className="font-medium">{selectedTerminal?.label || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Amount</span>
                <span className="font-semibold text-lg">
                  {fmtMoney(parseFloat(amount), currency)}
                </span>
              </div>
            </div>
          </div>
        ) : step === 'submitting' ? (
          <div className="py-10 flex flex-col items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
            <p className="text-sm text-slate-500">
              Initiating charge on {selectedTerminal?.label}…
            </p>
          </div>
        ) : step === 'result' ? (
          <div className="space-y-3" data-testid="charge-card-result">
            {result?.status === 'error' ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <AlertTriangle className="h-4 w-4" />Charge failed
                </div>
                <p className="text-xs text-red-800">
                  {result?.error_message || result?.error_code || 'Unknown provider error.'}
                </p>
              </div>
            ) : (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <ShieldCheck className="h-4 w-4" />Charge initiated
                </div>
                <div className="grid grid-cols-3 gap-y-1 text-xs mt-2">
                  <div className="text-emerald-700">Hub ref</div>
                  <div className="col-span-2 font-mono">{result?.hub_reference}</div>
                  <div className="text-emerald-700">Amount</div>
                  <div className="col-span-2 font-medium">
                    {fmtMoney(result?.amount, result?.currency)}
                  </div>
                  <div className="text-emerald-700">Terminal</div>
                  <div className="col-span-2">{result?.terminal_label}</div>
                  <div className="text-emerald-700">Provider ref</div>
                  <div className="col-span-2 font-mono">
                    {result?.provider_transaction_id || '—'}
                  </div>
                </div>
                {result?.provider_url ? (
                  <a
                    href={result.provider_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1 text-emerald-800 font-medium"
                    data-testid="charge-card-open-terminal-url"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />Open terminal URL
                  </a>
                ) : null}
              </div>
            )}
            <p className="text-xs text-slate-500">
              Timeline, Money Manager, and audit rows have been updated. A completion webhook from the provider will flip the status to
              <Badge variant="outline" className="text-[10px] mx-0.5">approved</Badge>
              or
              <Badge variant="outline" className="text-[10px] mx-0.5">declined</Badge>
              and attach the receipt automatically.
            </p>
          </div>
        ) : null}

        <DialogFooter>
          {step === 'form' ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={() => setStep('confirm')}
                disabled={!canProceed}
                data-testid="charge-card-review-btn"
              >
                Review
              </Button>
            </>
          ) : step === 'confirm' ? (
            <>
              <Button variant="outline" onClick={() => setStep('form')} disabled={submitting}>
                Back
              </Button>
              <Button
                onClick={() => submitCharge()}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="charge-card-submit-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                Confirm & Charge
              </Button>
            </>
          ) : step === 'result' ? (
            <Button onClick={() => onOpenChange(false)} data-testid="charge-card-done-btn">
              Done
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChargeCardDialog;
